"use client";
import { FormEvent, useState, useEffect, Suspense } from "react";
import { useSession, signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";

function LoginForm() {
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Form states
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // OTP states
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [otp, setOtp] = useState("");
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  const rawCallbackUrl = searchParams.get("callbackUrl");
  // Only allow relative URLs to prevent open redirect attacks
  const callbackUrl =
    rawCallbackUrl && rawCallbackUrl.startsWith("/")
      ? rawCallbackUrl
      : "/dashboard";

  useEffect(() => {
    const message = searchParams.get("message");
    if (message) {
      setSuccessMessage(message);
    }
  }, [searchParams]);

  // Redirect authenticated users to dashboard (or callbackUrl if present)
  useEffect(() => {
    if (session) {
      router.push(callbackUrl);
    }
  }, [session, router, callbackUrl]);

  // Resend cooldown timer
  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const timer = setTimeout(() => setOtpResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpResendCooldown]);

  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Step 1: Validate credentials and send OTP
  async function onCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginData.username,
          password: loginData.password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Email ou mot de passe incorrect");
        return;
      }

      // Test account bypass: sign in directly without OTP step
      if (data.skipOtp) {
        const result = await signIn("credentials", {
          username: loginData.username,
          otp: "",
          redirect: false,
        });
        if (result?.error) {
          setError("Erreur lors de la connexion");
        } else {
          router.push(callbackUrl);
        }
        return;
      }

      // Move to OTP step
      setStep("otp");
      setOtpResendCooldown(60);
      setSuccessMessage("Un code de vérification a été envoyé à votre adresse email.");
    } catch {
      setError("Une erreur est survenue lors de la connexion");
    } finally {
      setIsLoading(false);
    }
  }

  // Step 2: Verify OTP and sign in
  async function onOtpSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const result = await signIn("credentials", {
        username: loginData.username,
        otp,
        redirect: false,
      });

      if (result?.error) {
        setError("Code de vérification incorrect ou expiré");
      } else {
        router.push(callbackUrl);
      }
    } catch {
      setError("Une erreur est survenue lors de la vérification");
    } finally {
      setIsLoading(false);
    }
  }

  // Resend OTP
  async function handleResendOtp() {
    if (otpResendCooldown > 0) return;
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginData.username,
          password: loginData.password,
        }),
      });

      if (res.ok) {
        setOtpResendCooldown(60);
        setSuccessMessage("Un nouveau code a été envoyé.");
      } else {
        setError("Impossible de renvoyer le code. Veuillez réessayer.");
      }
    } catch {
      setError("Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  }

  // Mobile landing screen — sign up or use a laptop for the back-office
  if (isMobile && !session) {
    return (
      <div className="min-h-screen p-4 relative overflow-hidden flex flex-col items-center justify-center">
        <Image src="/background-web.png" alt="" fill className="object-cover" priority />
        <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-sm">
          <Image
            src="/e-del-care-white-no-bg.png"
            alt="E-DEL Care"
            width={260}
            height={52}
            className="object-contain"
            priority
          />

          <div className="bg-white rounded-[30px] shadow-xl p-7 w-full text-center">
            <h1 className="text-xl font-semibold mb-2">Bienvenue sur e-del !</h1>
            <p className="text-sm text-gray-500 mb-7">
              La plateforme de gestion des soins infirmiers à domicile.
            </p>

            <Link
              href="/signup"
              className="block w-full py-3 rounded-full btn-primary-gradient font-semibold text-sm uppercase mb-4"
            >
              Créer un compte
            </Link>

            <a
              href="https://play.google.com/store/apps/details?id=com.e_del_care.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-full border-2 border-[#004F96] text-[#004F96] font-semibold text-sm mb-6"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 shrink-0">
                <path d="M3.18 23.76c.3.17.65.19.97.07l12.43-7.18-2.7-2.7-10.7 9.81zm-1.12-20.6C2.01 3.4 2 3.67 2 3.95v16.1c0 .28.01.55.06.8l11.07-11.07-10.07-6.62zM20.49 10.45l-2.34-1.36-3.02 3.02 3.02 3.03 2.37-1.37c.68-.39 1.08-1.02 1.08-1.66s-.4-1.27-1.11-1.66zM4.15.24c-.32-.12-.67-.1-.97.07L13.37 9.4l-2.7 2.7L4.15.24z"/>
              </svg>
              Télécharger l&apos;application
            </a>

            <p className="text-xs text-gray-400 leading-relaxed">
              Déjà inscrit ? Le back-office e-del est accessible depuis un{" "}
              <span className="font-medium text-gray-600">ordinateur</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8 relative overflow-hidden">
      <Image
        src="/background-web.png"
        alt=""
        fill
        className="object-cover"
        priority
      />
      {!session && (
        // Unauthenticated state - show login form
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-6 py-8">
          <Image
            src="/e-del-care-white-no-bg.png"
            alt="E-DEL Care"
            width={400}
            height={80}
            className="object-contain w-[260px] sm:w-[400px] h-auto max-w-full"
            priority
          />
          <div className="w-full max-w-md bg-white p-6 sm:p-10 md:p-13 rounded-[30px] shadow-xl">
            <h1 className="text-2xl font-semibold mb-6 text-center">
              Bienvenue sur E-DEL !
            </h1>

            {step === "credentials" ? (
              <>
                <form onSubmit={onCredentialsSubmit} className="space-y-6">
                  <div className="flex items-center gap-3 border-b-2 border-black focus-within:border-[#004F96]">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      value={loginData.username}
                      onChange={handleLoginInputChange}
                      required
                      className="flex-1 min-w-0 px-0 py-2 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                      placeholder="exemple@email.com"
                    />
                    <label
                      htmlFor="username"
                      className="text-sm font-regular whitespace-nowrap text-gray-400 focus-within:text-[#004F96]"
                    >
                      email
                    </label>
                  </div>
                  <div className="flex items-center gap-3 border-b-2 border-black focus-within:border-[#004F96]">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={loginData.password}
                      onChange={handleLoginInputChange}
                      required
                      className="flex-1 min-w-0 px-0 py-2 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                      placeholder="••••••••"
                    />
                    <label
                      htmlFor="password"
                      className="text-sm font-regular whitespace-nowrap text-gray-400 focus-within:text-[#004F96]"
                    >
                      mot de passe
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      className="text-gray-400 hover:text-[#004F96] focus:outline-none"
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 15.338 6.91 18 12 18c1.338 0 2.57-.188 3.676-.53M6.228 6.228A10.451 10.451 0 0112 6c5.09 0 8.774 2.662 10.066 6-.46 1.193-1.202 2.273-2.168 3.173M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full rounded-full btn-primary-gradient uppercase font-semibold py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? "Vérification..." : "Se connecter"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Pas encore de compte ?{" "}
                    <Link
                      href="/signup"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Inscrivez-vous
                    </Link>
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 text-center mb-6">
                  Un code de vérification a été envoyé à <span className="font-semibold">{loginData.username}</span>
                </p>
                <form onSubmit={onOtpSubmit} className="space-y-6">
                  <div className="flex items-center gap-3 border-b-2 border-black focus-within:border-[#004F96]">
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      required
                      autoFocus
                      className="flex-1 min-w-0 px-0 py-2 text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-0 placeholder:text-gray-400 placeholder:text-base placeholder:tracking-normal"
                      placeholder="Entrez le code à 6 chiffres"
                    />
                  </div>
                  {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading || otp.length !== 6}
                    className="w-full rounded-full btn-primary-gradient uppercase font-semibold py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? "Vérification..." : "Vérifier"}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("credentials");
                      setOtp("");
                      setError("");
                      setSuccessMessage("");
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    &larr; Retour
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={otpResendCooldown > 0 || isLoading}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {otpResendCooldown > 0
                      ? `Renvoyer le code (${otpResendCooldown}s)`
                      : "Renvoyer le code"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen p-8 relative overflow-hidden flex items-center justify-center">
          {/* <LavaLampBackground variant="fullscreen" primaryColor="#004F96" secondaryColor="#00B375" /> */}
          <div className="relative z-10 text-white">Chargement...</div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
